import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { loginRequest, msalConfig } from './authConfig';
import { AuthConfiguration } from './authConfiguration';
import { User } from 'src/store/slices/users/types';
import usersApi from 'src/store/slices/users/api';

export interface IAuthService {
  init (): Promise<any>;
  login (): Promise<void>;
  logout (): Promise<void>;
  acquireAccessToken (): Promise<string>;
  getUser (): Promise<User>;
  getInternalUser (): User;
  isAuthenticated (): boolean;
}

export class AuthService implements IAuthService {
  private readonly _pca?: IPublicClientApplication;
  private readonly _apiScopes: string[];
  protected _user: User | undefined = undefined;

  constructor(authConfig: AuthConfiguration, apiScopes: string[]) {
    msalConfig.auth.authority = authConfig.authority;
    msalConfig.auth.clientId = authConfig.clientId;
    msalConfig.auth.knownAuthorities = [authConfig.domain];
    msalConfig.auth.postLogoutRedirectUri = authConfig.postLogoutRedirectUri;

    this._pca = new PublicClientApplication(msalConfig);
    this._apiScopes = apiScopes;
  }

  private get isAuthenticationEnabled (): boolean {
    return !!this._pca;
  }

  async init (): Promise<any> {
    if (!this.isAuthenticationEnabled) {
      return;
    }
    const pca = this._pca!;

    pca.addEventCallback(async (event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        pca.setActiveAccount(account);

        await this.acquireAccessToken();
      }

      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        await this.login();
      }
    });

    await pca.initialize();

    // When using redirect APIs,
    // handleRedirectPromise must be invoked when returning from the redirect.
    // https://github.com/AzureAD/microsoft-authentication-library-for-js
    // /blob/dev/lib/msal-browser/docs/errors.md#interaction_in_progress
    await pca.handleRedirectPromise();

    // Default to using the first account if no account is active on page load
    const accounts = pca.getAllAccounts();

    if (!pca.getActiveAccount() &&
      accounts.length > 0) {
      // Account selection logic is app dependent.
      // Adjust as needed for different use cases.
      pca.setActiveAccount(pca.getAllAccounts()[0]);
    }

    if (accounts.length === 0) {
      await this.login();
    }
  }

  //
  // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob
  // /dev/lib/msal-browser/docs/acquire-token.md
  //
  async acquireAccessToken (): Promise<string> {
    if (!this.isAuthenticationEnabled) {
      throw new Error('Authentication is not enabled.');
    }

    const activeAccount = this.activeAccount;

    if (!activeAccount) {
      await this.login();
      return '';
    }

    const request = {
      account: activeAccount,
      scopes: this._apiScopes,
    };

    const tokenResponse: AuthenticationResult =
      await this._pca!.acquireTokenSilent(request);

    return tokenResponse.accessToken;

  }

  private get activeAccount (): AccountInfo {
    const pca = this._pca!;
    const activeAccount = pca.getActiveAccount();
    const accounts = pca.getAllAccounts();

    return activeAccount ?? accounts[0];
  }

  async login (): Promise<void> {
    if (!this.isAuthenticationEnabled) {
      throw new Error('Authentication is not enabled.');
    }

    await this._pca!.loginRedirect(loginRequest);
  }

  async logout (): Promise<void> {
    if (!this.isAuthenticationEnabled) {
      throw new Error('Authentication is not enabled.');
    }

    const request = {
      account: this.activeAccount,
    };

    await this._pca!.logoutRedirect(request);
  }
  async getUser (): Promise<User> {
    if (!this._user) {
      const user = await usersApi.fetchUser();
      this._user = user.data;
    }

    return this._user;
  }
  getInternalUser (): User {
    const activeAccount = this.activeAccount;

    if (!activeAccount) {
      this.login();
      return {};
    }

    const newUser: User = {
      email: activeAccount.idTokenClaims?.emails![0] ?? '',
      displayName: activeAccount.idTokenClaims?.name,
    };
    return newUser;
  }
  isAuthenticated(): boolean {
    return this.activeAccount != undefined;
  }
}
